// @flow
/* eslint-disable flowtype/require-types-at-top */

import type {Node as ReactNode} from 'react';
import type {IntlShape} from 'react-intl';
import type {Column} from '../../../components/tables/types';
import type {
  DictionaryItem,
  DisputeResult,
  DisputeResultAccepted,
  DisputeResultLost,
  DisputeResultNew,
  DisputeResultNoActionsRequired,
  DisputeResultWon,
  DisputeRow,
  DisputeSort,
  PartnerEvidence
} from '../types';

import React from 'react';
import {Button, Pill, StatusDot, Tooltip} from '@xsolla/uikit';
import {usePartnerEvidence} from '../hooks/use-partner-evidence';
import {
  DISPUTE_SORT_DIRECTION_ASC,
  DISPUTE_SORT_DIRECTION_DESC,
  DISPUTE_SORT_FIELD_DISPUTE_DATE,
  DISPUTE_SORT_FIELD_TRANSACTION_DATE
} from '../types';

type ClassNamesFunc = (...classNames: Array<string>) => string;
type GetColumns = IntlShape => Array<Column<DisputeRow>>;
type PartnerEvidenceRendererProps = {
  merchantId?: ?number,
  disputeId: number,
  partnerEvidenceStatus?: ?string,
  children: ({partnerEvidence: ?PartnerEvidence, isLoading: boolean}) => ReactNode
};

const PartnerEvidenceRenderer = ({
  merchantId,
  disputeId,
  partnerEvidenceStatus,
  children
}: PartnerEvidenceRendererProps) => {
  const shouldFetch = Boolean(
    partnerEvidenceStatus &&
      (partnerEvidenceStatus === 'awaiting_response' ||
        partnerEvidenceStatus === 'evidence_received')
  );
  const {partnerEvidence, isLoading} = usePartnerEvidence({
    merchantId,
    disputeId,
    enabled: shouldFetch
  });
  return children({partnerEvidence, isLoading});
};

type InfoCellProps = {
  +cx: ClassNamesFunc,
  +stylePrefix: string,
  +value: DictionaryItem
};

const InfoCell = ({cx, stylePrefix, value}: InfoCellProps) => (
  <div className={cx(`${stylePrefix}-cell__content`, `${stylePrefix}-cell__content--vertical`)}>
    <div
      className={cx(
        `${stylePrefix}-cell__content`,
        `${stylePrefix}-cell__content--shadow`,
        `${stylePrefix}-cell__content--nowrap`
      )}>{`ID ${value.id}`}</div>
    <div className={cx(`${stylePrefix}-cell__content`)}>{value.name}</div>
  </div>
);

export const prepareColumnGetter = (
  cx: ClassNamesFunc,
  stylePrefix: string,
  sort: DisputeSort,
  changeSort: Function,
  filterFormName: string,
  protectUserIdField: boolean,
  openProofForm: Function,
  viewProof: Function,
  merchantId?: ?number
): GetColumns => intl => {
  const cols = [
    {
      id: 'transaction',
      name: `${intl.messages['app.support.disputes.table.transaction-id']}/${intl.messages['app.support.disputes.table.transaction-date']}`,
      sticky: true,
      fieldGetter: 'transaction',
      minWidth: 268,
      renderCell: option => {
        const {style, value, className} = option;
        const status = ((status: number) => {
          switch (status) {
            case 1:
              return 'testing';
            case 2:
              return 'processing';
            case 3:
              return 'enabled';
            case 4:
              return 'disabled';
            case 5:
              return 'error';
            case 6:
              return 'enabled-outline';
            case 7:
              return 'refunded';
            case 8:
              return 'refunded-outline';
            case 12:
              return 'processing-outline';
            case 16:
              return 'deactivated';
            default:
              return null;
          }
        })(value.status);

        return (
          <td className={cx(className, `${stylePrefix}-cell__wrapper--sticky`)} style={style}>
            <div
              className={cx(
                `${stylePrefix}-cell__content`,
                `${stylePrefix}-cell__content--vertical`
              )}>
              <div
                className={cx(
                  `${stylePrefix}-cell__content`,
                  `${stylePrefix}-cell__content--horizontal`
                )}>
                <Tooltip
                  position="top"
                  label={
                    intl.messages[
                      `app.support.disputes.table.transaction.status-${status || 'default'}`
                    ]
                  }>
                  <StatusDot status={status || 'deactivated'} />
                </Tooltip>
                <div
                  className={cx(
                    `${stylePrefix}-cell__content`,
                    `${stylePrefix}-cell__content--bold`
                  )}>
                  {value.id}
                </div>
              </div>
              <div
                className={cx(
                  `${stylePrefix}-cell__content`,
                  `${stylePrefix}-cell__content--vertical`
                )}>
                <div
                  className={cx(
                    `${stylePrefix}-cell__content`,
                    `${stylePrefix}-cell__content--nowrap`
                  )}>
                  {value.date.local}
                </div>
                <div
                  className={cx(
                    `${stylePrefix}-cell__content`,
                    `${stylePrefix}-cell__content--nowrap`
                  )}>{`(CST ${value.date.cst})`}</div>
              </div>
            </div>
          </td>
        );
      },
      renderHeadCell: option => {
        const cn = option.className.split(' ')[0];
        const buttonColor = sort.field === DISPUTE_SORT_FIELD_TRANSACTION_DATE ? 'blue' : 'gray';
        const buttonIcon =
          sort.field !== DISPUTE_SORT_FIELD_TRANSACTION_DATE ||
          sort.dir === DISPUTE_SORT_DIRECTION_DESC
            ? 'expand'
            : 'collapse';
        const buttonDir =
          sort.field === DISPUTE_SORT_FIELD_TRANSACTION_DATE &&
          sort.dir === DISPUTE_SORT_DIRECTION_DESC
            ? DISPUTE_SORT_DIRECTION_ASC
            : DISPUTE_SORT_DIRECTION_DESC;

        return (
          <th className={`${option.className}`} style={{...option.style, left: '0px'}}>
            <div className={cx(`${cn}__wrapper`, `${cn}__wrapper--fit`)}>
              <div className={`${cn}__border-bottom`} />
              <Button
                type="button"
                appearance="flatten"
                size="sm"
                icon={buttonIcon}
                initialIconColor={buttonColor}
                reversed="true"
                onClick={() =>
                  changeSort(
                    {
                      field: DISPUTE_SORT_FIELD_TRANSACTION_DATE,
                      dir: buttonDir
                    },
                    filterFormName
                  )
                }>
                <p className={`${cn}__text`}>{option.column.name}</p>
              </Button>
              <div className={cx(`${cn}__border`, `${cn}__border--sticky-column`)} />
            </div>
          </th>
        );
      }
    },
    {
      id: 'project',
      name: intl.messages['app.support.disputes.table.project'],
      fieldGetter: 'project',
      minWidth: 167,
      renderCell: option => {
        const {className, style, value} = option;
        return (
          <td className={cx(className)} style={style}>
            {value && <InfoCell cx={cx} stylePrefix={stylePrefix} value={value} />}
          </td>
        );
      }
    },
    {
      id: 'paymentMethod',
      name: intl.messages['app.support.disputes.table.payment-method'],
      fieldGetter: 'paymentMethod',
      minWidth: 167
    },
    {
      id: 'userDetails',
      name: intl.messages['app.support.disputes.table.user-details'],
      fieldGetter: 'userDetails',
      minWidth: 167,
      renderCell: option => {
        const {className, style, value} = option;
        return (
          <td className={cx(className)} style={style}>
            <InfoCell cx={cx} stylePrefix={stylePrefix} value={value} />
          </td>
        );
      }
    },
    {
      id: 'countryCode',
      name: intl.messages['app.support.disputes.table.country-code'],
      fieldGetter: 'countryCode',
      minWidth: 123
    },
    {
      id: 'transactionAmount',
      name: intl.messages['app.support.disputes.table.amount'],
      fieldGetter: 'transactionAmount',
      minWidth: 123,
      renderCell: option => {
        const {className, style, value} = option;
        const {currency} = value;
        return (
          <td className={cx(className)} style={style}>
            <span>{`${value.value} ${currency}`}</span>
          </td>
        );
      }
    },
    {
      id: 'fee',
      name: intl.messages['app.support.disputes.table.fee'],
      fieldGetter: 'fee',
      minWidth: 123,
      renderCell: option => {
        const {className, style, value} = option;
        const {fee, isFinal} = value;
        return (
          <td className={cx(className)} style={style}>
            <span>{`${fee.value} ${fee.currency}`}</span>
            {!isFinal && fee.value === 0 && (
              <Tooltip position="top" label={intl.messages['app.support.disputes.table.empty.fee']}>
                <span style={{color: 'red'}}>*</span>
              </Tooltip>
            )}
          </td>
        );
      }
    },
    {
      id: 'incomingDate',
      name: intl.messages['app.support.disputes.table.incoming-date'],
      fieldGetter: 'incomingDate',
      minWidth: 126,
      renderCell: option => {
        const {className, style, value} = option;
        return (
          <td className={cx(className)} style={style}>
            <div
              className={cx(
                `${stylePrefix}-cell__content`,
                `${stylePrefix}-cell__content--nowrap`
              )}>
              {value}
            </div>
          </td>
        );
      },
      renderHeadCell: option => {
        const cn = option.className.split(' ')[0];
        const buttonColor = sort.field === DISPUTE_SORT_FIELD_DISPUTE_DATE ? 'blue' : 'gray';
        const buttonIcon =
          sort.field !== DISPUTE_SORT_FIELD_DISPUTE_DATE || sort.dir === DISPUTE_SORT_DIRECTION_DESC
            ? 'expand'
            : 'collapse';
        const buttonDir =
          sort.field === DISPUTE_SORT_FIELD_DISPUTE_DATE && sort.dir === DISPUTE_SORT_DIRECTION_DESC
            ? DISPUTE_SORT_DIRECTION_ASC
            : DISPUTE_SORT_DIRECTION_DESC;

        return (
          <th className={`${option.className}`} style={{...option.style}}>
            <div className={cx(`${cn}__wrapper`, `${cn}__wrapper--fit`)}>
              <div className={`${cn}__border-bottom`} />
              <div className={`${cn}__border`} />
              <Button
                type="button"
                appearance="flatten"
                size="sm"
                icon={buttonIcon}
                initialIconColor={buttonColor}
                reversed="true"
                onClick={() =>
                  changeSort(
                    {
                      field: DISPUTE_SORT_FIELD_DISPUTE_DATE,
                      dir: buttonDir
                    },
                    filterFormName
                  )
                }>
                <p className={`${cn}__text`}>{option.column.name}</p>
              </Button>
            </div>
          </th>
        );
      }
    },
    {
      id: 'reason',
      name: intl.messages['app.support.disputes.table.reason'],
      fieldGetter: 'reason',
      minWidth: 151
    },
    {
      id: 'type',
      name: intl.messages['app.support.disputes.table.type'],
      fieldGetter: 'type',
      minWidth: 151
    },
    {
      id: 'result',
      name: intl.messages['app.support.disputes.table.result'],
      fieldGetter: 'result',
      minWidth: 187,
      renderCell: option => {
        const {value, style, className} = option;
        return (
          <td className={cx(className, `${stylePrefix}-cell`)} style={style}>
            {((result: DisputeResult) => {
              switch (result) {
                case (1: DisputeResultNew):
                  return (
                    <Pill appearance="branding">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.new']}
                      </div>
                    </Pill>
                  );
                case (2: DisputeResultAccepted):
                  return (
                    <Pill appearance="cancelled">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.accepted']}
                      </div>
                    </Pill>
                  );
                case (3: DisputeResultNeedProof):
                  return (
                    <Pill appearance="warning">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.need-proof']}
                      </div>
                    </Pill>
                  );
                case (4: DisputeResultProofSent):
                  return (
                    <Pill appearance="info">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.proof-sent']}
                      </div>
                    </Pill>
                  );
                case (5: DisputeResultNeedAddProof):
                  return (
                    <Pill appearance="warning">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.need-additional-proof']}
                      </div>
                    </Pill>
                  );
                case (6: DisputeResultNoActionsRequired):
                  return (
                    <Pill appearance="info">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.no-actions-required']}
                      </div>
                    </Pill>
                  );
                case (7: DisputeResultWon):
                  return (
                    <Pill appearance="active">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.won']}
                      </div>
                    </Pill>
                  );
                case (8: DisputeResultLost):
                  return (
                    <Pill appearance="error">
                      <div className={cx(`${stylePrefix}__pill`)}>
                        {intl.messages['app.support.disputes.table.result.lost']}
                      </div>
                    </Pill>
                  );
                default:
                  return null;
              }
            })(value)}
          </td>
        );
      }
    },
    {
      id: 'partner_evidence_response_text',
      name: intl.messages['app.support.disputes.table.partner_evidence_response_text'],
      fieldGetter: 'partner_evidence_response_text',
      minWidth: 151,
      renderCell: option => {
        const {className, style, row} = option;
        return (
          <td className={cx(className)} style={style}>
            <PartnerEvidenceRenderer
              merchantId={merchantId}
              disputeId={row.disputeId}
              partnerEvidenceStatus={row.partner_evidence_status}>
              {({partnerEvidence, isLoading}) => {
                if (isLoading) {
                  return '—';
                }
                return partnerEvidence?.response_text || '';
              }}
            </PartnerEvidenceRenderer>
          </td>
        );
      }
    },
    {
      id: 'partner_evidence_days_left',
      name: intl.messages['app.support.disputes.table.partner_evidence_days_left'],
      fieldGetter: 'partner_evidence_days_left',
      minWidth: 80,
      renderCell: option => {
        const {className, style, row} = option;
        return (
          <td className={cx(className)} style={style}>
            <PartnerEvidenceRenderer
              merchantId={merchantId}
              disputeId={row.disputeId}
              partnerEvidenceStatus={row.partner_evidence_status}>
              {({partnerEvidence, isLoading}) => {
                if (isLoading || !partnerEvidence) {
                  return '—';
                }
                const needsResponse = partnerEvidence.status === 'awaiting_response';
                if (!needsResponse) {
                  return '—';
                }
                const remaining = partnerEvidence.days_left;
                if (remaining <= 0) {
                  return 0;
                }
                return remaining;
              }}
            </PartnerEvidenceRenderer>
          </td>
        );
      }
    },
    {
      id: 'is_action_required',
      fieldGetter: 'is_action_required',
      minWidth: 50,
      tooltip: intl.messages['app.support.disputes.table.is-action-required'],
      renderHeadCell: option => {
        const {style, className} = option;
        return (
          <th className={cx(className, `${stylePrefix}__head`)} style={style}>
            <div className="custom-tooltip-wrapper">
              <Tooltip label={intl.messages['app.support.disputes.table.action-tooltip']} />
            </div>
          </th>
        );
      },
      renderCell: option => {
        const {className, style, row} = option;

        return (
          <td className={cx(className)} style={style}>
            <PartnerEvidenceRenderer
              merchantId={merchantId}
              disputeId={row.disputeId}
              partnerEvidenceStatus={row.partner_evidence_status}>
              {({partnerEvidence}) => {
                const status = partnerEvidence?.status || row.partner_evidence_status;
                const needsResponse = status === 'awaiting_response';
                const isStatusEvidenceReceived = status === 'evidence_received';

                return (
                  <div
                    className={cx(
                      `${stylePrefix}-cell__content`,
                      `${stylePrefix}-cell__content--center`
                    )}>
                    {needsResponse && (
                      <Tooltip
                        label={intl.messages['app.support.disputes.table.action-button-tooltip']}>
                        <Button
                          size="md"
                          icon="message"
                          appearance="square"
                          initialIconColor="blue"
                          onClick={() => {
                            openProofForm(row.disputeId, row.transaction.id);
                          }}
                        />
                      </Tooltip>
                    )}
                    {isStatusEvidenceReceived && (
                      <Tooltip
                        label={intl.messages['app.support.disputes.table.view-proof-tooltip']}>
                        <Button
                          size="md"
                          icon="message"
                          appearance="square"
                          initialIconColor="gray"
                          onClick={() => {
                            viewProof(row.disputeId, row.transaction.id, partnerEvidence);
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                );
              }}
            </PartnerEvidenceRenderer>
          </td>
        );
      }
    }
  ];

  if (protectUserIdField) {
    cols.splice(
      cols.findIndex(col => col.id === 'userDetails'),
      1
    );
  }

  return cols;
};
